import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  usePendingDeferredBookingInquiries,
  useDecideDeferredBookingInquiry,
  useDeferredBookingInquiryList,
} from '../hooks/useDeferredBookingInquiries';
import { useSupportCaseList } from '../hooks/useSupportCases';
import { DeferredBookingStatusBadge } from '../components/DeferredBookingStatusBadge';
import { DeferredBookingDetailPanel } from '../components/DeferredBookingDetailPanel';
import { SupportCaseStatusBadge } from '../components/SupportCaseStatusBadge';
import { ListRefreshButton } from '../components/ListRefreshButton';
import type { DeferredBookingInquiry, DeferredBookingDecisionOutcome } from '../types/deferredBookingInquiry';
import type { DeferredInquiryDecisionStatus } from '../types/supportCase';
import { NON_FINAL_SUPPORT_CASE_STATUSES } from '../types/supportCase';
import { formatDate } from '../utils/formatDate';
import { isAuthRequiredError } from '../utils/listRefreshState';

type FilterMode = 'ALL' | 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';

const FILTER_STATUSES: Record<Exclude<FilterMode, 'ALL'>, DeferredInquiryDecisionStatus> = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
  EXPIRED: 'EXPIRED',
};

const DeferredBookingInquiries = () => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'badges', 'navigation']);

  const [filterMode, setFilterMode] = useState<FilterMode>('ALL');
  const [selectedInquiry, setSelectedInquiry] = useState<DeferredBookingInquiry | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [lastOutcome, setLastOutcome] = useState<DeferredBookingDecisionOutcome | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Pending inquiries (legacy endpoint — accept/decline)
  const {
    data: pendingInquiries = [],
    isLoading: pendingLoading,
    isError: pendingError,
    isFetching: pendingFetching,
    refetch: refetchPending,
  } = usePendingDeferredBookingInquiries();

  // All inquiries (B2 endpoint — for decided filters + links)
  const decisionStatuses = filterMode !== 'ALL' && filterMode !== 'PENDING'
    ? [FILTER_STATUSES[filterMode]]
    : undefined;

  const {
    data: inquiryPage,
    isLoading: listLoading,
    isError: listError,
    isFetching: listFetching,
    refetch: refetchList,
  } = useDeferredBookingInquiryList(
    decisionStatuses ? { decisionStatus: decisionStatuses, sort: 'decisionAt,desc' } : undefined,
  );

  // Non-final support cases for case link rendering
  const { data: nonFinalCasesPage } = useSupportCaseList({
    statuses: [...NON_FINAL_SUPPORT_CASE_STATUSES],
  });
  const nonFinalCasesByBookingId = new Map(
    (nonFinalCasesPage?.content ?? []).map(c => [c.bookingId, c]),
  );

  const {
    mutate: decideInquiry,
    isPending: isDeciding,
  } = useDecideDeferredBookingInquiry();

  const isLoading = pendingLoading || listLoading;
  const isError = pendingError || listError;
  const isFetching = pendingFetching || listFetching;

  const handleRefresh = async () => {
    try {
      setRefreshError(null);
      await Promise.all([
        refetchPending({ throwOnError: true }),
        refetchList({ throwOnError: true }),
      ]);
    } catch (error) {
      setRefreshError(isAuthRequiredError(error) ? t('common:signInRequired') : t('common:refreshFailed'));
    }
  };

  const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'object' && error !== null && 'response' in error) {
      const status = (error as { response: { status: number } }).response?.status;
      if (status === 400) return t('errors:decisionValidationFailed');
      if (status === 403) return t('errors:decisionUnauthorized');
      if (status === 404) return t('errors:bookingNotFound');
      if (status === 409) return t('errors:decisionConflict');
    }
    return t('errors:somethingWentWrong');
  };

  const handleAccept = (bookingId: string) => {
    setActionError(null);
    decideInquiry(
      { bookingId, request: { decision: 'ACCEPT' } },
      {
        onSuccess: (outcome) => {
          setLastOutcome(outcome);
          setSelectedInquiry(null);
        },
        onError: (error) => setActionError(getErrorMessage(error)),
      },
    );
  };

  const handleDecline = (bookingId: string, reason: string) => {
    setActionError(null);
    decideInquiry(
      { bookingId, request: { decision: 'DECLINE', declineReason: reason } },
      {
        onSuccess: (outcome) => {
          setLastOutcome(outcome);
          setSelectedInquiry(null);
        },
        onError: (error) => setActionError(getErrorMessage(error)),
      },
    );
  };

  const filterButtons: Array<{ mode: FilterMode; label: string }> = [
    { mode: 'ALL', label: t('pages:filterAll') },
    { mode: 'PENDING', label: t('pages:filterPending') },
    { mode: 'ACCEPTED', label: t('pages:filterAccepted') },
    { mode: 'DECLINED', label: t('pages:filterDeclined') },
    { mode: 'EXPIRED', label: t('pages:filterExpired') },
  ];

  // Decide which rows to display
  const displayedInquiries =
    filterMode === 'ALL' || filterMode === 'PENDING'
      ? pendingInquiries
      : (inquiryPage?.content ?? []).map(oi => ({
          // map OwnerDeferredBookingInquiry → DeferredBookingInquiry shape
          bookingId: oi.bookingId ?? '',
          apartmentId: oi.apartmentId,
          status: oi.currentBookingStatus,
          checkInDate: oi.stay.checkInDate,
          checkOutDate: oi.stay.checkOutDate,
          nights: null,
          totalPrice: null,
          pricePerNight: null,
          ownerReviewExpiresAt: oi.ownerReviewExpiresAt,
          guestActionExpiresAt: null,
          guestInfo: {
            guestId: null,
            firstName: oi.guest.fullName.split(' ')[0] ?? null,
            lastName: oi.guest.fullName.split(' ').slice(1).join(' ') || null,
            fullName: oi.guest.fullName,
            email: oi.guest.email,
            phone: null,
            numberOfGuests: null,
          },
          tripDetails: null,
          _ownerInquiry: oi,
        } as DeferredBookingInquiry & { _ownerInquiry: typeof oi }));

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages:deferredInquiriesHeading')}</h1>
        <p className="text-gray-600 mt-2">{t('pages:deferredInquiriesSubHeading')}</p>
      </div>

      {/* Filter buttons */}
      <div className="flex gap-2 mb-4">
        {filterButtons.map(({ mode, label }) => (
          <button
            key={mode}
            onClick={() => setFilterMode(mode)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              filterMode === mode
                ? 'bg-primary-600 text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex justify-end mb-4">
        <ListRefreshButton isRefreshing={isFetching} onRefresh={handleRefresh} />
      </div>

      {refreshError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{refreshError}</span>
          <button onClick={handleRefresh} className="flex items-center gap-1 text-sm font-medium hover:text-red-900">
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {actionError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-600 hover:text-red-800">×</button>
        </div>
      )}

      {isError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{t('errors:failedToLoadInquiries')}</span>
          <button onClick={() => { refetchPending(); refetchList(); }} className="flex items-center gap-1 text-sm font-medium hover:text-red-900">
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {isLoading && (
        <div className="card flex justify-center py-16">
          <span className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!isLoading && (
        <>
          {displayedInquiries.length === 0 ? (
            <div className="card">
              <p className="text-gray-600 text-center py-12">{t('pages:noDeferredInquiries')}</p>
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 text-xs uppercase tracking-wide">
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnGuest')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnEmail')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnCheckIn')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnCheckOut')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnStatus')}</th>
                    <th className="pb-3 font-medium">{t('pages:columnActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayedInquiries.map(inquiry => {
                    const bookingId = inquiry.bookingId;
                    const activeCase = bookingId ? nonFinalCasesByBookingId.get(bookingId) : undefined;
                    const isPending = inquiry.status === 'PENDING_OWNER_CONFIRMATION';

                    return (
                      <tr
                        key={bookingId || inquiry.guestInfo?.email}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => {
                          if (isPending) {
                            setSelectedInquiry(inquiry);
                            setLastOutcome(null);
                            setActionError(null);
                          }
                        }}
                      >
                        <td className="py-3 pr-4 text-gray-700">{inquiry.guestInfo?.fullName ?? '—'}</td>
                        <td className="py-3 pr-4 text-gray-600">{inquiry.guestInfo?.email ?? '—'}</td>
                        <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                          {inquiry.checkInDate ? formatDate(inquiry.checkInDate) : '—'}
                        </td>
                        <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                          {inquiry.checkOutDate ? formatDate(inquiry.checkOutDate) : '—'}
                        </td>
                        <td className="py-3 pr-4">
                          <DeferredBookingStatusBadge status={inquiry.status ?? ''} />
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-3">
                            {bookingId && (
                              <Link
                                to={`/bookings?bookingId=${bookingId}`}
                                onClick={e => e.stopPropagation()}
                                className="text-xs text-primary-600 hover:underline"
                              >
                                {t('pages:inquiryBookingLink')}
                              </Link>
                            )}
                            {activeCase && (
                              <Link
                                to={`/support-cases?caseId=${activeCase.caseId}`}
                                onClick={e => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-xs"
                              >
                                <SupportCaseStatusBadge status={activeCase.status} />
                              </Link>
                            )}
                            {!bookingId && (
                              <span className="text-xs text-gray-400">{t('pages:inquiryNoBooking')}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {selectedInquiry && (
        <DeferredBookingDetailPanel
          inquiry={selectedInquiry}
          isAccepting={isDeciding}
          isDeclining={isDeciding}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onClose={() => {
            setSelectedInquiry(null);
            setLastOutcome(null);
          }}
          lastOutcome={lastOutcome}
        />
      )}
    </div>
  );
};

export default DeferredBookingInquiries;
